import { AlertCircle, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import {
  ProgressRing,
  type ProgressRingSize,
} from "@/components/ui/progress-ring";

export function Spinner({
  label = "Loading…",
  className,
  size = "md",
  /** Center in available space (page / panel loaders). Default true. */
  center = true,
}: {
  label?: string;
  className?: string;
  size?: ProgressRingSize;
  center?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-3 text-sm text-text-muted",
        center && "min-h-[12rem] w-full flex-col justify-center py-10",
        !center && "inline-flex",
        className,
      )}
      role="status"
      aria-live="polite"
      data-testid="spinner"
    >
      <ProgressRing size={size} label={label} labelSrOnly />
      <span>{label}</span>
    </div>
  );
}

/** Full-viewport or main-area loading with Progress Ring. */
export function PageLoading({
  label = "Loading…",
  className,
}: {
  label?: string;
  className?: string;
}) {
  return (
    <main
      className={cn(
        "flex min-h-screen flex-col items-center justify-center gap-3 bg-background",
        className,
      )}
      role="status"
      aria-live="polite"
      data-testid="page-loading"
    >
      <ProgressRing size="lg" label={label} labelSrOnly />
      <span className="text-sm text-text-muted">{label}</span>
    </main>
  );
}

export { ProgressRing };

export function ErrorBox({ error }: { error: unknown }) {
  const msg =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "Something went wrong";
  return (
    <Alert variant="destructive">
      <AlertCircle className="h-4 w-4" />
      <AlertDescription>{msg}</AlertDescription>
    </Alert>
  );
}

export function SuccessBox({ children }: { children: React.ReactNode }) {
  return (
    <Alert variant="success">
      <CheckCircle2 className="h-4 w-4" />
      <AlertDescription>{children}</AlertDescription>
    </Alert>
  );
}

export function WarnBox({ children }: { children: React.ReactNode }) {
  return (
    <Alert variant="warning">
      <AlertCircle className="h-4 w-4" />
      <AlertDescription>{children}</AlertDescription>
    </Alert>
  );
}

export function PageHeader({
  title,
  description,
  actions,
  eyebrow,
}: {
  title: string;
  description?: string;
  actions?: React.ReactNode;
  eyebrow?: string;
}) {
  return (
    <div className="page-header page-enter">
      <div className="page-header-copy">
        {eyebrow ? (
          <div className="page-header-eyebrow">
            <div className="eyebrow">{eyebrow}</div>
          </div>
        ) : null}
        <h1 className="page-title">
          {title}
        </h1>
        {description ? (
          <p className="page-description">
            {description}
          </p>
        ) : null}
      </div>
      {actions ? (
        <div className="page-header-actions">{actions}</div>
      ) : null}
    </div>
  );
}

export function EmptyState({
  children,
  title,
  icon,
}: {
  children: React.ReactNode;
  title?: string;
  icon?: React.ReactNode;
}) {
  return (
    <Card className="border-dashed">
      <CardContent className="flex flex-col items-center px-6 py-14 text-center">
        {icon ? (
          <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-sm border border-border-standard bg-surface-container-low text-text-muted">
            {icon}
          </div>
        ) : null}
        {title ? (
          <div className="font-sans text-base font-semibold text-text-primary">
            {title}
          </div>
        ) : null}
        <div className={cn("max-w-sm text-sm text-text-muted", title && "mt-2")}>
          {children}
        </div>
      </CardContent>
    </Card>
  );
}

/** @deprecated Use AppShell branding instead */
export function BrandRail() {
  return (
    <aside className="brand-rail" aria-label="TinyBase">
      <div className="brand-rail-mark" title="TinyBase">
        tb
      </div>
      <div className="brand-rail-word">TinyBase</div>
      <div
        className="mb-5 mt-auto h-1.5 w-1.5 rounded-full bg-secondary"
        aria-hidden
      />
    </aside>
  );
}

export function StatTile({
  label,
  value,
  hint,
  trend,
}: {
  label: string;
  value: string | number;
  hint?: string;
  trend?: "up" | "down" | "neutral";
}) {
  return (
    <div className="stat-tile">
      <div className="text-[11px] font-bold uppercase tracking-label text-text-muted">
        {label}
      </div>
      <div className="mt-2 font-sans text-xl font-semibold tabular-nums tracking-tight text-text-primary">
        {value}
      </div>
      {hint ? (
        <div
          className={cn(
            "mt-1 text-xs",
            trend === "up" && "text-success",
            trend === "down" && "text-error",
            (!trend || trend === "neutral") && "text-text-disabled",
          )}
        >
          {hint}
        </div>
      ) : null}
    </div>
  );
}
